/**
 * ItemGroup API 함수들
 */

import { AuthContextType } from "../auth/AuthContext";
import { apiWithTimeout } from "../../shared/api/timeout";
import { apiHost, isPayloadError, getErrorMessage } from "../../shared/api/helpers";

export interface ItemGroupSummary {
    id: string;
    name: string;
    description?: string | null;
    sort_order: number;
}

// 쿼리 함수들 (TanStack Query용)
export async function fetchItemGroupListFn() {
    const response = await apiWithTimeout.quick.get(`${apiHost}/api/itemgroups/`);
    return response.data;
}

export async function fetchItemGroupFn(uid: string) {
    const response = await apiWithTimeout.quick.get(`${apiHost}/api/itemgroups/${uid}/`);
    return response.data;
}

// 뮤테이션 함수들 (TanStack Query용)
export async function updateItemGroupFn(opts: { uid: string; payload: any }) {
    const { uid, payload } = opts;
    await apiWithTimeout.normal.patch(`${apiHost}/api/itemgroups/${uid}/`, payload);
}

export async function createItemGroupFn(payload: { name: string }): Promise<ItemGroupSummary> {
    const response = await apiWithTimeout.normal.post(`${apiHost}/api/itemgroups/`, payload);
    return response.data;
}

export async function deleteItemGroupFn(uid: string) {
    await apiWithTimeout.normal.delete(`${apiHost}/api/itemgroups/${uid}/`);
}

// 레거시 함수들 (기존 코드와의 호환성)
export async function fetchItemGroupList(opts: {
    auth: AuthContextType,
    uid?: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, setErrorMessage, setResult } = opts;
    try {
        const data = await fetchItemGroupListFn();
        if (data && setResult) {
            setResult(data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function retrieveItemGroup(opts: {
    auth: AuthContextType,
    uid?: string,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
}) {
    const { auth, setErrorMessage, setResult } = opts;
    try {
        const data = await fetchItemGroupFn(opts.uid!);
        if (data && setResult) {
            setResult(data);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            await auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message);
        } else {
            console.error(message);
        }
    }
}

export async function updateItemGroup(opts: {
    uid: string,
    payload: any,
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { uid, payload, auth, setErrorMessage, setProcessMessage, setError } = opts;
    try {
        await updateItemGroupFn({ uid, payload });
        opts.setResult && opts.setResult()

    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response.data
        if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, {type: "custom", message: value});
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function createItemGroup(opts: {
    auth: AuthContextType,
    payload: any,
    setProcessMessage?: (arg?: any) => void;
    setErrorMessage?: (arg?: any) => void;
    setResult?: (arg?: any) => void;
    setError?: (...args: any) => void;
}) {
    const { payload, auth, setErrorMessage, setProcessMessage, setResult, setError } = opts;

    try {
        const created = await createItemGroupFn(payload);
        if (setResult) {
            setResult(created);
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }
        const errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        const errorData = error.response.data
        if (isPayloadError(errorData, payload) && setError) {
            for (const [key, value] of Object.entries(errorData)) {
                setError(key, {type: "custom", message: value});
            }
        } else if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
    } finally {
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}

export async function deleteItemGroup(opts: {
    uid: string,
    auth: AuthContextType,
    setErrorMessage?: (arg: any) => void;
    setProcessMessage?: (arg: any) => void;
    setResult?: () => void;
}) {
    const { uid, auth, setErrorMessage, setProcessMessage, setResult } = opts;

    try {
        await deleteItemGroupFn(uid);
        if (setResult) {
            setResult();
        }
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
            return;
        }

        let errorMessage = "시스템 오류입니다. 잠시 후 다시 시도하세요";
        try {
            const errorData = error.response?.data;
            if (errorData && errorData.errors && errorData.errors.length > 0) {
                errorMessage = errorData.errors[0];
            }

        } catch (parseError) {
            // pass
        }

        if (setErrorMessage) {
            setErrorMessage(errorMessage);
        }
        if (setProcessMessage) {
            setProcessMessage(null);
        }
    }
}
